import { useState } from 'react'
import type { Demand, Requirement, RequirementCategory } from '@/types/knowledge_graph'
import { PencilIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { REQUIREMENT_CATEGORY_LABELS } from '@/constants/requirementCategory'
import { DemandRefBadge } from './DemandRefBadge'
import { InferenceTypeBadge } from './InferenceTypeBadge'
import type { InferenceTypeMeta } from './InferenceTypeBadge'

const REQUIREMENT_CATEGORY_CLASS: Record<RequirementCategory, string> = {
  functional: 'bg-(--bp-accent-pale) text-(--bp-accent)',
  non_functional: 'bg-(--bp-warning-pale) text-(--bp-warning)',
  business_rule: 'bg-(--bp-accent-pale) text-(--bp-ok)',
  constraint: 'bg-(--bp-accent-pale) text-(--bp-ink3)'
}

function inferenceTypeTag (type: string): InferenceTypeMeta {
  const map: Record<string, InferenceTypeMeta> = {
    explicit: {
      icon: '🗣️',
      label: '顧客が明示',
      style: { background: 'var(--bp-accent-pale)', color: 'var(--bp-accent)' }
    },
    required: {
      icon: '⚙️',
      label: '論理的に必須',
      style: { background: 'var(--bp-ok-pale, #dcfce7)', color: 'var(--bp-ok)' }
    },
    industry_standard: {
      icon: '🌐',
      label: '業界標準',
      style: { background: 'var(--bp-surface2)', color: 'var(--bp-ink2)' }
    },
    hypothesis: {
      icon: '⚠️',
      label: '要確認の仮説',
      style: { background: 'var(--bp-warning-pale)', color: 'var(--bp-warning)' }
    },
    implicit: {
      icon: '💭',
      label: '推論で補完',
      style: { background: 'var(--bp-accent-pale)', color: 'var(--bp-ai)' }
    }
  }
  return map[type] ?? { icon: '❓', label: type, style: { background: 'var(--bp-surface2)', color: 'var(--bp-ink3)' } }
}

export function RequirementRow ({
  requirement,
  demands,
  onUpdate
}: {
  requirement: Requirement
  demands: Demand[]
  onUpdate: (changes: Partial<Requirement>) => void
}): React.ReactElement {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftCategory, setDraftCategory] = useState<RequirementCategory>('functional')
  const isApproved = requirement.review_status === 'approved'

  function startEdit (): void {
    setDraftName(requirement.name)
    setDraftDescription(requirement.description)
    setDraftCategory(requirement.category)
    setEditing(true)
  }

  function commitEdit (): void {
    const trimmedName = draftName.trim()
    if (trimmedName === '') return
    const changes: Partial<Requirement> = {}
    if (trimmedName !== requirement.name) changes.name = trimmedName
    const trimmedDesc = draftDescription.trim()
    if (trimmedDesc !== requirement.description) changes.description = trimmedDesc
    if (draftCategory !== requirement.category) changes.category = draftCategory
    if (Object.keys(changes).length > 0) onUpdate(changes)
    setEditing(false)
  }

  function cancelEdit (): void { setEditing(false) }

  const firstDemandId = requirement.related_demand_ids?.[0]

  if (editing) {
    return (
      <tr>
        <td colSpan={6} className='p-2 px-3'>
          <div className='flex flex-col gap-1.5'>
            <div className='flex gap-1.5 items-center'>
              <input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
                  if (e.key === 'Escape') cancelEdit()
                }}
                placeholder='要件名'
                className='flex-1 text-xs font-semibold border border-(--bp-accent) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink) outline-none'
              />
              <select
                value={draftCategory}
                onChange={(e) => setDraftCategory(e.target.value as RequirementCategory)}
                className='text-[11px] border border-(--bp-border) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink2)'
              >
                <option value='functional'>機能要件</option>
                <option value='non_functional'>非機能要件</option>
                <option value='business_rule'>業務ルール</option>
                <option value='constraint'>制約</option>
              </select>
            </div>
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelEdit()
              }}
              placeholder='説明'
              rows={2}
              className='w-full resize-y text-[11px] border border-(--bp-border) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink2) outline-none'
            />
            <div className='flex gap-1 justify-end'>
              <button
                onClick={cancelEdit}
                className='text-[11px] px-2 py-0.5 border border-(--bp-border) rounded bg-(--bp-surface2) text-(--bp-ink2) cursor-pointer'
              >
                キャンセル
              </button>
              <button
                onClick={commitEdit}
                className='text-[11px] px-2 py-0.5 border-none rounded bg-(--bp-accent) text-white cursor-pointer'
              >
                保存
              </button>
            </div>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>
        <input
          type='checkbox'
          checked={isApproved}
          className='size-3.5 accent-(--bp-ok) cursor-pointer'
          onChange={() => onUpdate({ review_status: isApproved ? 'pending' : 'approved' })}
        />
      </td>
      <td className='font-mono text-xs tabular-nums'>
        <span className='text-(--bp-ink3)'>
        {requirement.id}
        </span>
      </td>
      <td>
        <div className='w-22 shrink-0'>
          {(requirement.inference_type != null) && (
            <InferenceTypeBadge
              tag={inferenceTypeTag(requirement.inference_type)}
              reasoning={requirement.reasoning}
            />
          )}
        </div>
      </td>
      <td className='text-xs'>
        <div className='flex gap-2 items-baseline'>
          <div className='flex-1'>
            <div className='font-medium'>{requirement.name}</div>
            {requirement.description !== '' && (
              <div className='mt-0.5 text-[11px] text-(--bp-ink3) leading-relaxed'>
                {requirement.description}
              </div>
            )}
          </div>
        </div>
      </td>
      <td>
        <span className={cn('inline-block px-2 py-0.5 rounded text-[10px] font-semibold', REQUIREMENT_CATEGORY_CLASS[requirement.category])}>
          {REQUIREMENT_CATEGORY_LABELS[requirement.category]}
        </span>
      </td>
      <td className='font-mono text-xs text-(--bp-ink3) tabular-nums'>
        {firstDemandId != null
          ? (
            <DemandRefBadge
              demandId={firstDemandId}
              demand={demands.find((d) => d.id === firstDemandId)}
            />
            )
          : '—'}
      </td>
      <td>
        <button
          title='要件を修正'
          onClick={startEdit}
          className='static size-6 flex items-center justify-center bg-(--bp-surface2) border border-(--bp-border) rounded-[5px] cursor-pointer text-(--bp-ink3) p-0 transition-colors hover:bg-(--bp-accent-pale) hover:text-(--bp-accent) hover:border-[rgba(53,71,193,.3)]'
        >
          <PencilIcon width={12} height={12} />
        </button>
      </td>
    </tr>
  )
}
